import Layout from "../../components/Layout/Layout";
import { Fragment, useState, useEffect, useCallback } from "react";
import AccountServices from "../../services/AccountServices";
import { Dialog, Transition } from "@headlessui/react";
import fire from "./../../utils/Toastr";
import MandateUploadButton from "./mandateUploadButton";
import { FaCheck, FaTimes, FaSpinner, FaExpand, FaDownload } from "react-icons/fa";

const statusStyles = {
  true: "bg-black text-white",
  false: "bg-white text-black",
  Success: "bg-green-500 text-white",
  Approved: "bg-green-500 text-white",
  Rejected: "bg-red-500 text-white",
  Pending: "bg-yellow-500 text-white",
  AwaitingApproval: "bg-blue-500 text-white",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function CustomerDocument({ userName: userNameProp }) {
  const userName =
    userNameProp || new URLSearchParams(window.location.search).get("id");
  const [acct, setAcct] = useState([]);
  const [docList, setDocList] = useState([]);
  const [doc, setDoc] = useState({});
  const [acctType, setAccountType] = useState("");
  const [info, setInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [adminType, setAdminType] = useState("");
  const [expandedImage, setExpandedImage] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);

  const listDoc = useCallback(async () => {
    const resp = await AccountServices.listDocument(userName);
    const data = resp?.data || {};
    const accounts = data.customerAccounts || [];
    const documents = data.documentsLists || [];
    setDoc(data);
    setAcct([...accounts]);
    setDocList([...documents]);
    setAccountType(documents.length > 0 ? documents[0].accountClass || "" : "");
    var details = sessionStorage.getItem("user");
    var userDetails = JSON.parse(details);
    var type = userDetails.adminType;
    setAdminType(type);
  }, [userName]);

  const getInfo = useCallback(async () => {
    const resp = await AccountServices.personalInfo(userName);
    setInfo(resp.data);
  }, [userName]);

  const updateDoc = async () => {
    try {
      if (!selectedDoc) {
        fire("error", "No document selected");
        return;
      }

      setLoading(true);
      const resp = await AccountServices.updateDoc({
        documentId: selectedDoc.documentId,
        documentTag: selectedDoc.documentTag,
        userName: userName,
      });

      if (resp) {
        fire("success", resp.message);
        // Keep the dialog open until reload
        setTimeout(() => {
          setShowApproveDialog(false);
          setExpandedImage(null);
          setSelectedDoc(null);
          window.location.reload();
        }, 1500); // Give user time to see success message
      }
    } catch (error) {
      fire("error", "Failed to approve document");
      setLoading(false); // Don't close dialog on error
    }
  };

  const rejectDoc = async () => {
    try {
      if (!selectedDoc) {
        fire("error", "No document selected");
        return;
      }

      setLoading(true);
      const resp = await AccountServices.rejectDoc({
        documentId: selectedDoc.documentId,
        documentTag: selectedDoc.documentTag,
        userName: userName,
        comments:
          rejectionReason || "Document rejected without specified reason",
      });

      if (resp) {
        fire("success", resp.message);
        // Keep the dialog open until reload
        setTimeout(() => {
          setShowRejectDialog(false);
          setExpandedImage(null);
          setSelectedDoc(null);
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      fire("error", "Failed to reject document");
      setLoading(false); // Don't close dialog on error
    }
  };

  const handleDownloadImage = () => {
    if (!expandedImage) return;
    const link = document.createElement("a");
    link.href = expandedImage;
    link.download = `document-${selectedDoc?.documentTag || "image"}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const discardInfo = async (update) => {
    const resp = await AccountServices.discardInfo({
      infomationToUpdate: update,
      userName: userName,
    });
    if (resp) {
      fire("success", resp.message);
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  };

  const handleApproveClick = async () => {
    if (!selectedDoc) {
      fire("error", "No document selected");
      return;
    }
    setShowApproveDialog(true);
  };

  const handleRejectClick = () => {
    if (!selectedDoc) {
      fire("error", "No document selected");
      return;
    }
    setShowRejectDialog(true);
    setRejectionReason(selectedDoc.comments || "");
  };

  const handleImageClick = (doc) => {
    if (!doc) {
      console.error("No document data provided");
      return;
    }
    setSelectedDoc(doc);
    setExpandedImage(doc.uploadUrl || null);
  };

  useEffect(() => {
    listDoc();
    getInfo();
  }, [listDoc, getInfo]);

  const content = (
    <>
      <main>
          <div className="relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white mt-5">
            <div className="contents min-w-full py-2 align-middle md:px-6 lg:px-8 bg-white">
              {/* form */}
              <div className="p-6">
                <div className="flex justify-between">
                  <h4 className="font-bold">ACCOUNT INFORMATION</h4>
                </div>
                {acct.length === 0 ? (
                  <div className=" mt-8">
                    {" "}
                    No account available for display.{" "}
                  </div>
                ) : (
                  <>
                    {acct.map((acct, i) => (
                      <>
                        <div className="mt-6 grid ">
                          <div key={i} className=" ">
                            <div className="flex">
                              <div className="mr-3 w-1/2">
                                <label
                                  htmlFor="account"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Account Name {i + 1}
                                </label>
                                <div className="mt-1">
                                  <input
                                    type="text"
                                    name="account"
                                    id="account"
                                    readOnly
                                    value={`${acct.firstName} ${acct.otherNames} ${acct.lastName}`}
                                    className="shadow-sm bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                              <div className="mr-3 w-1/4">
                                <label
                                  htmlFor="account"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Account Number {i + 1}
                                </label>
                                <div className="mt-1">
                                  <input
                                    type="text"
                                    name="account"
                                    id="account"
                                    readOnly
                                    value={acct.accountNumber}
                                    className="shadow-sm bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                              <div className="mr-3 w-1/2">
                                <label
                                  htmlFor="accountType"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Account Class {i + 1}
                                </label>
                                <div className="mt-1">
                                  <input
                                    type="text"
                                    name="accountType"
                                    id="accountType"
                                    readOnly
                                    value={acct.accountClass}
                                    className="shadow-sm bg-gray-100 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                              <div className=" w-1/2">
                                {acct.mandateUploaded === false ? (
                                  <MandateUploadButton
                                    accountNumber={acct.accountNumber}
                                    adminType={adminType}
                                  />
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="my-6">
              <h2 className="font-semibold text-gray-900">
                {acctType === "DOMIND"
                  ? "Required Uploads"
                  : "Required Uploads (Savings)"}
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {docList.map((document, index) => (
                <div
                  key={`${document.documentTag}-${index}`}
                  className="col-span-1 flex flex-col text-center bg-white rounded-lg shadow-md divide-y divide-gray-200 relative"
                >
                  {/* Status badge */}
                  <div
                    className={`absolute top-2 right-2 px-2 py-1 rounded-md text-xs font-medium ${
                      statusStyles[document.status] ||
                      "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {document.status === "Success"
                      ? "Approved"
                      : document.status}
                  </div>

                  {document.uploadUrl ? (
                    <div className="flex-1 flex flex-col p-4">
                      <div
                        className="flex justify-center cursor-pointer relative"
                        onClick={() => handleImageClick(document)}
                      >
                        <img
                          className="w-32 h-32 object-contain mx-auto hover:opacity-75 transition-opacity"
                          src={document.uploadUrl}
                          alt={document.documentDescription}
                        />
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded">
                          <FaExpand className="h-4 w-4" />
                        </div>
                      </div>
                      <h3 className="mt-4 text-gray-900 text-sm font-medium">
                        {document.documentDescription}
                      </h3>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col p-4">
                      <div className="flex justify-center items-center h-32">
                        <span className="text-gray-400">
                          No document uploaded
                        </span>
                      </div>
                      <h3 className="mt-4 text-gray-900 text-sm font-medium">
                        {document.documentDescription}
                      </h3>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Required details */}
          <div className="mt-5">
            <div className="mt-5">
              <p>Required Details</p>
              <p className="text-gray-400">
                Please indicate if customer has provided the following details
              </p>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8">
              <div className=" flex-col col-span-4 flex  border-r-4 pr-5 border-gray-300">
                <p className="uppercase">personal information</p>
                <div className="mt-5 grid grid-cols-1  sm:gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Marital Status?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          name="maritalStatus"
                          className={classNames(
                            statusStyles[info.maritalStatus],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes{" "}
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("MaritalStatus")}
                          className={classNames(
                            statusStyles[!info.maritalStatus],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Mother Maiden's Name?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          name="motherSMaidenName"
                          className={classNames(
                            statusStyles[info.motherSMaidenName],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("MotherMaidensName")}
                          className={classNames(
                            statusStyles[!info.motherSMaidenName],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">National ID Number?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nationalId],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NationalId")}
                          className={classNames(
                            statusStyles[!info.nationalId],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Are you a Nigerian?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nationality],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("IsNigerian")}
                          className={classNames(
                            statusStyles[!info.nationality],
                            " inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Resident Permit Number?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.residentPermitNumber],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("ResidentPermitNumber")}
                          className={classNames(
                            statusStyles[!info.residentPermitNumber],
                            " inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-col col-span-4 flex  ml-4">
                <p className="uppercase">next of kin</p>
                <div className="mt-5 grid grid-cols-1  sm:gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  <div className="border border-md  rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Surname?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinSurname],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark px-4 py-2 text-sm font-medium   shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinSurname")}
                          className={classNames(
                            statusStyles[!info.nextOfKinSurname],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">First Name?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinFirstname],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinFirstname")}
                          className={classNames(
                            statusStyles[!info.nextOfKinFirstname],
                            " inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Middle Name?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinMiddlename],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinMiddlename")}
                          className={classNames(
                            statusStyles[!info.nextOfKinMiddlename],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Phone Number?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinPhoneNumber],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinPhoneNumber")}
                          className={classNames(
                            statusStyles[!info.nextOfKinPhoneNumber],
                            " inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Email address?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinEmailAddress],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinEmailAddress")}
                          className={classNames(
                            statusStyles[!info.nextOfKinEmailAddress],
                            " inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Date of birth?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinDob],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinDOB")}
                          className={classNames(
                            statusStyles[!info.nextOfKinDob],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Gender?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinGender],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinGender")}
                          className={classNames(
                            statusStyles[!info.nextOfKinGender],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">State of residence?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinStateofResidence],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            discardInfo("NextOfKinStateofResidence")
                          }
                          className={classNames(
                            statusStyles[!info.nextOfKinStateofResidence],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Residential Address?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinResidentialAddress],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            discardInfo("NextOfKinResidentialAddress")
                          }
                          className={classNames(
                            statusStyles[!info.nextOfKinResidentialAddress],
                            " inline-flex items-center justify-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-4 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Relationship?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className={classNames(
                            statusStyles[info.nextOfKinRelationship],
                            "mr-3 inline-flex items-center justify-center rounded-md border border-dark px-4 py-2 text-sm font-medium shadow-sm sm:w-auto"
                          )}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => discardInfo("NextOfKinRelationship")}
                          className={classNames(
                            statusStyles[!info.nextOfKinRelationship],
                            " inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium  shadow-sm sm:w-auto"
                          )}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Corporate details */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8">
              <div className=" flex-col col-span-6 flex ">
                <p className="uppercase">business information</p>
                <div className="mt-5 grid grid-cols-2  sm:gap-3 sm:grid-cols-2 lg:grid-cols-6">
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Nature of Business?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Sector/Industry?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2  flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">
                        Location of registered business?
                      </p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">
                        Operating business address?
                      </p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">
                        Registered Business address?
                      </p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Business Phone Number?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Business Email Address?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">Business Website ?</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="border border-md rounded-md shadow-md flex-col col-span-2 flex  p-2 text-center">
                    <div className="my-5">
                      <p className="mb-2 text-xs">SCUML Registration Number</p>
                      <div className="flex items-center align-center justify-center">
                        <button
                          type="button"
                          className="mr-3 inline-flex items-center justify-center rounded-md border border-dark btn-secondary px-4 py-2 text-sm font-medium text-dark shadow-sm sm:w-auto"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className=" inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Expanded Image Dialog */}
        <Transition.Root show={!!expandedImage} as={Fragment}>
          <Dialog
            as="div"
            className="fixed z-50 inset-0 overflow-y-auto"
            onClose={() => {
              setExpandedImage(null);
            }}
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                        onClick={() => {
                          setExpandedImage(null);
                          setSelectedDoc(null);
                        }}
                      >
                        <FaTimes className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <img
                        src={expandedImage}
                        alt="Expanded document view"
                        className="w-full h-auto max-h-[60vh] object-contain"
                      />

                      {/* Show rejection comment if document is rejected */}
                      {selectedDoc?.status === "Rejected" &&
                        selectedDoc?.comments && (
                          <div className="mt-4 p-3 bg-red-50 rounded-md">
                            <h4 className="font-medium text-red-800">
                              Rejection Reason:
                            </h4>
                            <p className="text-red-600">
                              {selectedDoc.comments}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    {expandedImage && (
                      <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={handleDownloadImage}
                      >
                        <FaDownload className="mr-1.5 h-4 w-4" />
                        Download
                      </button>
                    )}
                    {selectedDoc &&
                      selectedDoc.status === "AwaitingApproval" && (
                        <>
                          <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={handleRejectClick}
                            disabled={loading}
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={handleApproveClick}
                            disabled={loading}
                          >
                            Approve
                          </button>
                        </>
                      )}
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Approve Confirmation Dialog */}
        <Transition.Root show={showApproveDialog} as={Fragment}>
          <Dialog
            as="div"
            className="fixed z-50 inset-0 overflow-y-auto"
            onClose={() => setShowApproveDialog(false)}
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                        onClick={() => setShowApproveDialog(false)}
                      >
                        <FaTimes className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                        <FaCheck className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <Dialog.Title
                          as="h3"
                          className="text-lg leading-6 font-medium text-gray-900"
                        >
                          Confirm Approval
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to approve this document?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={updateDoc}
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <FaSpinner className="animate-spin mr-2" />
                          Approving...
                        </div>
                      ) : (
                        "Confirm Approval"
                      )}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => setShowApproveDialog(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Rejection Reason Dialog */}
        <Transition.Root show={showRejectDialog} as={Fragment}>
          <Dialog
            as="div"
            className="fixed z-50 inset-0 overflow-y-auto"
            onClose={() => setShowRejectDialog(false)}
          >
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              </Transition.Child>

              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 focus:outline-none"
                        onClick={() => setShowRejectDialog(false)}
                      >
                        <FaTimes className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <FaTimes className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <Dialog.Title
                          as="h3"
                          className="text-lg leading-6 font-medium text-gray-900"
                        >
                          Rejection Reason
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Please provide a reason for rejecting this document.
                          </p>
                          <textarea
                            rows={4}
                            className="mt-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter rejection reason..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                        rejectionReason.trim() === ""
                          ? "bg-red-300 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                      }`}
                      onClick={() => {
                        if (rejectionReason.trim() !== "") {
                          setLoading(true);
                          rejectDoc();
                        }
                      }}
                      disabled={loading || rejectionReason.trim() === ""}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <FaSpinner className="animate-spin mr-2" />
                          Rejecting...
                        </div>
                      ) : (
                        "Confirm Rejection"
                      )}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => setShowRejectDialog(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      </>
  );

  if (userNameProp) {
    return content;
  }

  return (
    <>
      <Layout
        title={"Customer Documents"}
        pageTag={"customer"}
        current={"/customer/document"}
      >
        {content}
      </Layout>
    </>
  );
}
